import { sanitizeStrings } from './helpers/sanitizeStrings'
import { AddToCartEvent } from './schemas/multiple_teams/addToCart.schema'
import { RemoveFromCartEvent } from './schemas/purchase/buy/removeFromCart.schema'
import { ViewCartEvent } from './schemas/purchase/buy/viewCart.schema'

type Basket = {
  id: string
  size: number
  action: string
  promotionName?: string
  totalDiscount?: number
}

type BasketEvent = ViewCartEvent | AddToCartEvent | RemoveFromCartEvent

type UpdateInitiator = BasketEvent['basket_data']['update_initiator']

export type Product = {
  item_id: string
  item_name: string
  coupon?: string
  discount?: number
  item_brand: string
  item_category?: string
  item_category2?: string
  item_category3?: string
  item_category4?: string
  item_category5?: string
  item_list_id?: string
  item_list_name?: string
  item_variant?: string
  design_id?: string
  price: number
  quantity: number
  item_cart_index?: number
  is_sponsored?: 'true' | 'false' | 'internal'
}

type ProductCartEventDetails = {
  basket: Basket
  products: Product[]
  action: string
  currency: string
  event: 'add_to_cart' | 'remove_from_cart' | 'view_cart'
  totalValue: number
  label: string
  productIndex?: number
  listName?: string
  eventCategory?: string
}

export const createBasketEvent = ({
  event,
  basket,
  products,
  action,
  currency,
  totalValue,
  label,
  productIndex,
  eventCategory,
}: ProductCartEventDetails): BasketEvent => {
  let basketSize = basket.size

  if (event === 'add_to_cart') {
    basketSize = basket.size - products[0].quantity
  } else if (event === 'remove_from_cart') {
    basketSize = basket.size + products[0].quantity
  }

  return {
    event,
    basket_data: {
      id: basket.id,
      size: basketSize,
      update_initiator: sanitizeStrings(basket.action) as UpdateInitiator,
    },
    ecommerce: {
      currency: currency.toLocaleUpperCase() as 'AUD' | 'GBP' | 'EUR' | 'USD',
      value: totalValue,
      coupon: basket.promotionName?.toLowerCase(),
      discount: basket.totalDiscount,
      items: products.map((product, index) => ({
        coupon: product.coupon?.toLowerCase(),
        discount: product.discount,
        index: product.item_cart_index || productIndex || index + 1,
        item_brand: product.item_brand,
        item_category: sanitizeStrings(product.item_category),
        item_category2: sanitizeStrings(product.item_category2),
        item_category3: sanitizeStrings(product.item_category3),
        item_category4: sanitizeStrings(product.item_category4),
        item_category5: sanitizeStrings(product.item_category5),
        item_id: product.item_id.toLocaleLowerCase(),
        item_list_id: sanitizeStrings(product.item_list_id),
        item_list_name: sanitizeStrings(product.item_list_name),
        item_name: sanitizeStrings(product.item_name),
        item_variant: sanitizeStrings(product.item_variant),
        design_id: product.design_id,
        price: product.price,
        quantity: product.quantity,
        is_sponsored: product.is_sponsored || 'false',
      })),
    },
    event_data: {
      action: sanitizeStrings(action),
      category: eventCategory
        ? eventCategory.toLocaleLowerCase()
        : 'product action',
      label: label.toLocaleLowerCase(),
      non_interaction: true,
      value: Math.round(totalValue),
    },

    // Web only //
    discount_data: undefined,

    // For GTM //
    application_data: {
      datalayer_version: 1,
    },
  }
}
